/**
 * 69.4 step 6a, build kits (the step 6 design note in BUILDLIST 69.4; DECISIONS 166 (4), the owner: "some concept of some
 * consumible thing that explains why you can't build more than `n` configured of a given item"; 171 (Q1) the proposal
 * taken as the default), with GameConfig.buildKits: every build kind is a side's consumable count — 65.23's grenades' and
 * 167's vehicles' pattern, a side's total.
 * - **The kits** (`BUILD_KITS`, dials): the FOB's is the side's `fobsLeft` (69.4's count, now set from the kit), the HMG's,
 *   the relay's and the beacon's `state.buildKits[team]`. A build spends one as it stands; a structure lost refunds nothing.
 * - **The relay and the beacon** are still carried by who plants them (`MatchSquad.relays`, `PlayerState.beacons`): the
 *   setup's issue (RELAY.carried a squad, BEACON.carried its first unit) is drawn from the side's kits in squad order while
 *   they last, and a unit asking for supply at its side's crate (`stepKitRestock`; a FOB's only while linked, as the pooled
 *   stocks) tops its squad's relays — and, the squad's beacon carrier, its beacon — back up from them.
 * - **The HMG's kit** is read by 6e (the HMG as a build kind).
 * - **With build costs** (6b, GameConfig.buildCosts): a relay or beacon restocked also pays its cost from the nearest store
 *   of the side within reach that holds enough, as a build would; as many as the stores pay for, relays first.
 * Pure; nothing here runs without the switch.
 */
import type { GameState } from '../types';
import { BUILD_KITS, RELAY, BEACON } from '../constants';
import { nearestTeamCrate, supplyReach } from './supply';
import { servesPool } from './fob';
import { squadOf } from './squads';
import { costsOn, storeFor, spend, buildCost } from './buildCosts';

export type KitEvent = { kind: 'kitRestocked'; unitId: number; structure: 'relay' | 'beacon'; count: number };

/** Are build kits on in this match? */
export function kitsOn(config: Pick<GameState['config'], 'buildKits'>): boolean {
  return !!config.buildKits;
}

/** A side's kits left of a carried kind (the HMG's, the relay's, the beacon's); the FOB's is `fobsLeft`. */
export function kitsLeft(state: Pick<GameState, 'buildKits'>, team: number, kind: 'hmg' | 'relay' | 'beacon'): number {
  return state.buildKits?.[team]?.[kind] ?? 0;
}

/**
 * The setup's kits, after the setup's issue of relays and beacons and its FOB count: each side's from BUILD_KITS; the FOB's
 * count set from its kit; the relays and beacons issued drawn from the kits in squad order (a squad, its carrier, past
 * the kit gets none).
 */
export function withBuildKits(state: GameState): GameState {
  if (!kitsOn(state.config)) return state;
  const teams = [...new Set(state.players.map(p => p.teamIndex))].sort((a, b) => a - b);
  const buildKits: NonNullable<GameState['buildKits']> = {};
  for (const t of teams) buildKits[t] = { hmg: BUILD_KITS.hmg, relay: BUILD_KITS.relay, beacon: BUILD_KITS.beacon };
  const squads = (state.squads ?? []).map(sq => {
    const kit = buildKits[sq.team];
    if (!kit || sq.relays === undefined) return sq;
    const give = Math.min(sq.relays, kit.relay);
    kit.relay -= give;
    return { ...sq, relays: give };
  });
  const players = [...state.players].sort((a, b) => a.id - b.id).reduce((out, p) => {
    const kit = buildKits[p.teamIndex];
    if (!kit || p.beacons === undefined) return out;
    const give = Math.min(p.beacons, kit.beacon);
    kit.beacon -= give;
    return give === p.beacons ? out : out.map(x => (x.id === p.id ? { ...x, beacons: give } : x));
  }, state.players);
  const fobsLeft = state.fobsLeft ? Object.fromEntries(Object.keys(state.fobsLeft).map(t => [t, BUILD_KITS.fob])) : undefined;
  return { ...state, buildKits, ...(state.squads ? { squads } : {}), players, ...(fobsLeft ? { fobsLeft } : {}) };
}

/**
 * One tick's restock of the carried kits: each unit asking (`resupply`) on its feet within reach of a crate of its side
 * (a FOB's only while linked) tops its squad's relays back up to RELAY.carried (with relays on) and — its squad's beacon
 * carrier, the squad's first unit — its beacon to BEACON.carried (with beacons on), from the side's kits, in unit order.
 */
export function stepKitRestock(state: GameState, asks: ReadonlyArray<boolean | undefined>): { state: GameState; events: KitEvent[] } | null {
  if (!state.buildKits) return null;
  let next = state;
  const events: KitEvent[] = [];
  for (let i = 0; i < state.players.length; i++) {
    const p = next.players[i];
    if (!asks[i] || !p.alive || p.airborne || p.mount) continue;
    const sq = squadOf(next, p.id);
    const relayShort = next.config.relays && sq && sq.relays !== undefined ? Math.max(0, RELAY.carried - sq.relays) : 0;
    const carrier = !!sq && Math.min(...sq.unitIds) === p.id;
    const beaconShort = next.config.beacons && carrier ? Math.max(0, BEACON.carried - (p.beacons ?? 0)) : 0;
    if (relayShort <= 0 && beaconShort <= 0) continue;
    if (!nearestTeamCrate(next, p, supplyReach(next), s => s.health > 0 && servesPool(next, s))) continue;
    const kit = next.buildKits![p.teamIndex];
    if (!kit) continue;
    let relays = Math.min(relayShort, kit.relay), beacons = Math.min(beaconShort, kit.beacon);
    // 69.4 step 6b's follow-up (with build costs): each one drawn pays its cost from the nearest store of the side that
    // can (core/buildCosts.ts) — as many as the stores pay for, relays first
    if (costsOn(next.config)) {
      let paidState: GameState = next;
      const pay = (kind: 'relay' | 'beacon', want: number) => {
        let got = 0;
        for (; got < want; got++) {
          const store = storeFor(paidState, p.teamIndex, p, buildCost(kind));
          if (!store) break;
          const r = spend(paidState, store, buildCost(kind));
          paidState = { ...paidState, structures: r.structures, convoys: r.convoys };
        }
        return got;
      };
      relays = pay('relay', relays);
      beacons = pay('beacon', beacons);
      next = paidState;
    }
    if (relays <= 0 && beacons <= 0) continue;
    next = {
      ...next,
      buildKits: { ...next.buildKits, [p.teamIndex]: { ...kit, relay: kit.relay - relays, beacon: kit.beacon - beacons } },
      ...(relays > 0 && sq ? { squads: (next.squads ?? []).map(s => (s.id === sq.id ? { ...s, relays: (s.relays ?? 0) + relays } : s)) } : {}),
      ...(beacons > 0 ? { players: next.players.map(x => (x.id === p.id ? { ...x, beacons: (x.beacons ?? 0) + beacons } : x)) } : {}),
    };
    if (relays > 0) events.push({ kind: 'kitRestocked', unitId: p.id, structure: 'relay', count: relays });
    if (beacons > 0) events.push({ kind: 'kitRestocked', unitId: p.id, structure: 'beacon', count: beacons });
  }
  return events.length ? { state: next, events } : null;
}
